// Раздел «iikoCloud» в настройках администратора: привязки ключей iikoCloud к серверам iiko.
// Ключ вводится только здесь и уходит на бэкенд; обратно приходят лишь последние 4 символа.
// Строка подключения открывает окно подключения (как окно сервера): сведения и действия.
import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyIcon from '@mui/icons-material/Key';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import StoreMallDirectoryIcon from '@mui/icons-material/StoreMallDirectory';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncIcon from '@mui/icons-material/Sync';
import VerifiedIcon from '@mui/icons-material/Verified';

import ResponsiveDialog from './ResponsiveDialog';
import { useDesktopLayout } from './layout/AppLayout';
import { getMonogram } from './ServerList';
import { primaryButtonSx, secondaryButtonSx, footerButtonSx } from './ServerForm';
import { PasswordField } from './form/FormFields';
import CollapsibleSection from './settings/CollapsibleSection';
import {
  ACCENT,
  ActionRow,
  ChoiceList,
  DialogHeader,
  ERROR_COLOR,
  Pill,
  SUCCESS_COLOR,
  SectionTitle,
  StatusText,
  ToggleRow,
  WARNING_COLOR,
  smallButtonSx,
} from './settings/SettingsControls';
import {
  checkIikoCloudKey,
  createIikoCloudConnection,
  deleteIikoCloudConnection,
  fetchIikoCloudGroupMap,
  fetchIikoCloudPlaces,
  fetchIikoCloudRestaurantMap,
  fetchIikoCloudTerminalGroups,
  fetchIikoCloudTerminalGroupsByKey,
  listIikoCloudConnections,
  listIikoCloudServers,
  recheckIikoCloudConnection,
  refreshIikoCloudDictionaries,
  replaceIikoCloudKey,
  saveIikoCloudGroupMap,
  saveIikoCloudPlaces,
  saveIikoCloudRestaurantMap,
  updateIikoCloudConnection,
} from '../services/dataService';
import { extractErrorMessage } from '../utils/errors';
import { ACCORDION_BUTTON_SX, CARD_LABEL_COLOR, DIVIDER_COLOR, MUTED_TEXT_COLOR } from '../styles/tokens';

const NEUTRAL_COLOR = '#a1a1aa';

const dangerButtonSx = {
  ...footerButtonSx,
  backgroundColor: '#ef5350',
  color: '#fff',
  fontWeight: 700,
  '&:hover': { backgroundColor: '#e53935', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#ef5350', color: '#fff', opacity: 0.7 },
};

const formatTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '—';

const minutes = (seconds) => Math.max(1, Math.ceil(seconds / 60));

const groupsSummary = (ids) => (ids == null ? 'все группы' : `групп: ${ids.length}`);

// null — все организации ключа (и новые); одна — её название
const organizationsSummary = (organizations) => {
  if (organizations == null) return 'все организации';
  if (organizations.length === 1) return organizations[0].name || organizations[0].id;
  return `организаций: ${organizations.length}`;
};

// Ошибка актуальна, пока после неё не было успеха (или пока ключ не принят). В БД она остаётся для разбора
const hasFreshError = (c) =>
  Boolean(c.last_error) && (c.key_invalid || !c.last_ok_at || !c.last_error_at || c.last_error_at > c.last_ok_at);

// Заметная доля позиций стоп-листа без названия: номенклатура не загрузилась (код ошибки) или блюд в ней нет
const nomenclatureIssueText = ({ unnamed, total, error }) =>
  `${unnamed} из ${total} позиций стоп-листа без названия — ` +
  (error ? `номенклатура iikoCloud не загружается (${error})` : 'этих блюд нет в номенклатуре iikoCloud');

// Главный статус подключения: от важного к фоновому
export function connectionStatus(c) {
  if (!c.enabled) return { label: 'Выключено', color: NEUTRAL_COLOR };
  if (c.key_invalid) return { label: 'Ключ не принят', color: ERROR_COLOR };
  // Пауза после ошибок — по группам методов: отчёты (olap) отдельно, остальное подключение работает
  const backoff = c.pauses?.backoff || {};
  const paused = Object.keys(backoff);
  if (paused.length > 0) {
    const what = paused.length === 1 && paused[0] === 'olap' ? 'Пауза отчётов' : 'Пауза';
    return { label: `${what} · ${minutes(Math.max(...Object.values(backoff)))} мин`, color: WARNING_COLOR };
  }
  if (Object.keys(c.pauses?.rate_limited || {}).length > 0) return { label: 'Лимит iikoCloud', color: WARNING_COLOR };
  if (hasFreshError(c)) return { label: 'Ошибка', color: WARNING_COLOR };
  return { label: 'Работает', color: SUCCESS_COLOR };
}

function Footer({ children }) {
  return <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>{children}</Box>;
}

function BusyLabel({ busy, children }) {
  return busy ? <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.6)' }} /> : children;
}

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
      <CircularProgress size={24} sx={{ color: ACCENT }} />
    </Box>
  );
}

// «Все» (и новые тоже) или выбранные — для организаций и групп терминалов. Список виден всегда
function AllOrSelectedToggles({ idPrefix, allLabel, emptyText, items, all, selected, onAllChange, onToggle, disabled }) {
  return (
    <Box>
      <ToggleRow
        id={`${idPrefix}-all`}
        first
        label={allLabel}
        hint="В том числе новые"
        checked={all}
        disabled={disabled}
        onChange={onAllChange}
      />
      {items.length === 0 ? (
        <Typography sx={{ fontSize: '14px', color: MUTED_TEXT_COLOR, py: 1 }}>{emptyText}</Typography>
      ) : (
        items.map((item) => (
          <ToggleRow
            key={item.id}
            id={`${idPrefix}-${item.id}`}
            label={item.name || item.id}
            hint={item.hint}
            checked={all || selected.includes(item.id)}
            disabled={disabled || all}
            onChange={() => onToggle(item.id)}
          />
        ))
      )}
    </Box>
  );
}

// Группы терминалов; у нескольких организаций — с названием организации в подсказке
function GroupsToggles({ groups, organizations, allGroups, selected, onAllChange, onToggle, disabled }) {
  const names = Object.fromEntries((organizations || []).map((o) => [o.id, o.name]));
  const several = new Set(groups.map((g) => g.organization_id)).size > 1;
  const items = groups.map((g) => ({
    id: g.id,
    name: g.name,
    hint: [several ? names[g.organization_id] : null, g.in_sleep ? 'спящий режим' : null].filter(Boolean).join(' · ') || undefined,
  }));
  return (
    <AllOrSelectedToggles
      idPrefix="iikocloud-group"
      allLabel="Все группы терминалов"
      emptyText="У организаций нет групп терминалов."
      items={items}
      all={allGroups}
      selected={selected}
      onAllChange={onAllChange}
      onToggle={onToggle}
      disabled={disabled}
    />
  );
}

// Организации ключа: у сети на одном сервере (ЦО) каждая — ресторан
function OrganizationsToggles({ organizations, allOrganizations, selected, onAllChange, onToggle, disabled }) {
  return (
    <AllOrSelectedToggles
      idPrefix="iikocloud-org"
      allLabel="Все организации"
      emptyText="По этому ключу нет организаций."
      items={organizations}
      all={allOrganizations}
      selected={selected}
      onAllChange={onAllChange}
      onToggle={onToggle}
      disabled={disabled}
    />
  );
}

const toggleIn = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

// Пауза iikoCloud (наша защита после ошибок или 429): секунд до её конца из ошибки бэкенда, иначе null
const retryAfterOf = (err) => {
  const detail = err?.response?.data?.detail;
  const seconds = Number(detail?.retry_after);
  return detail?.source === 'iikocloud' && seconds > 0 ? seconds : null;
};

// Загрузка данных окна из iikoCloud: при ошибке — текст и время конца паузы, reload — запросить снова
function useCloudLoad(load, deps) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [retryAt, setRetryAt] = useState(null); // Date.now() конца паузы
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setStatus(null);
    setRetryAt(null);
    load()
      .then((result) => alive && setData(result))
      .catch((err) => {
        if (!alive) return;
        setStatus({ type: 'error', text: extractErrorMessage(err) });
        const seconds = retryAfterOf(err);
        setRetryAt(seconds ? Date.now() + seconds * 1000 : null);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  return { data, setData, status, setStatus, retryAt, reload: () => setAttempt((n) => n + 1) };
}

const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

// Вместо «Сохранить», пока данных нет из-за ошибки: таймер паузы iikoCloud, после неё — «Запросить»
function RetryButton({ retryAt, onRetry }) {
  const left = () => (retryAt ? Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)) : 0);
  const [seconds, setSeconds] = useState(left);

  useEffect(() => {
    setSeconds(left());
    if (!retryAt) return undefined;
    const timer = setInterval(() => {
      const next = left();
      setSeconds(next);
      if (next === 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryAt]);

  return (
    <Button type="button" onClick={onRetry} disabled={seconds > 0} sx={primaryButtonSx}>
      {seconds > 0 ? `Повторить через ${clock(seconds)}` : 'Запросить'}
    </Button>
  );
}

// --- раздел ---

export default function IikoCloudAdmin({ token, sx }) {
  return (
    <CollapsibleSection id="iikocloud" title="iikoCloud" sx={sx}>
      <IikoCloudConnections token={token} />
    </CollapsibleSection>
  );
}

function IikoCloudConnections({ token }) {
  const [connections, setConnections] = useState(null);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [conns, srvs] = await Promise.all([listIikoCloudConnections(token), listIikoCloudServers(token)]);
      setConnections(conns);
      setServers(srvs);
      setStatus(null);
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const replace = (updated) => setConnections((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  const opened = connections?.find((c) => c.id === openId) || null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: '14px', color: MUTED_TEXT_COLOR, minWidth: 0 }}>
          Ключ клиента действует для всех пользователей его сервера.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Обновить список">
            <span>
              <IconButton onClick={load} disabled={loading} aria-label="Обновить список" sx={{ width: 44, height: 44, color: CARD_LABEL_COLOR }}>
                {loading ? <CircularProgress size={18} sx={{ color: ACCENT }} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={smallButtonSx}>
            Подключить
          </Button>
        </Box>
      </Box>

      <StatusText status={status} sx={{ mb: 1 }} />

      {connections === null ? (
        loading && <Loading />
      ) : connections.length === 0 ? (
        <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.55)' }}>
          Подключений пока нет. Подключите сервер клиента с отдельным ключом API iikoCloud.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {connections.map((c) => (
            <ConnectionRow key={c.id} connection={c} onOpen={() => setOpenId(c.id)} />
          ))}
        </Box>
      )}

      <CreateConnectionDialog
        open={createOpen}
        token={token}
        servers={servers}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          setCreateOpen(false);
          setStatus({ type: 'success', text: 'Подключение iikoCloud создано.' });
          await load();
        }}
      />

      <ConnectionDialog
        connection={opened}
        token={token}
        onClose={() => setOpenId(null)}
        onChanged={replace}
        onDeleted={async () => {
          setOpenId(null);
          setStatus({ type: 'success', text: 'Подключение удалено.' });
          await load();
        }}
      />
    </Box>
  );
}

function ConnectionRow({ connection: c, onOpen }) {
  const status = connectionStatus(c);
  return (
    <ButtonBase
      onClick={onOpen}
      aria-label={`${c.server_name}: ${status.label}. Открыть подключение`}
      sx={{
        width: '100%',
        minHeight: 56,
        boxSizing: 'border-box',
        p: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderRadius: '10px',
        textAlign: 'left',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
        '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${ACCENT}` },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: 800,
          backgroundColor: 'rgba(255,255,255,0.06)',
          color: '#a1a1aa',
        }}
      >
        {getMonogram(c.server_name)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>
          {c.server_name}
        </Typography>
        <Typography noWrap sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
          {organizationsSummary(c.organizations)} · {groupsSummary(c.terminal_group_ids)}
        </Typography>
      </Box>
      <Pill color={status.color}>{status.label}</Pill>
      <ChevronRightIcon aria-hidden="true" sx={{ color: MUTED_TEXT_COLOR, flexShrink: 0 }} />
    </ButtonBase>
  );
}

// --- подключение сервера ---

const emptyCreate = {
  serverKey: '',
  apiKey: '',
  organizations: null, // после «Проверить ключ»
  allOrganizations: true, // по умолчанию все организации ключа, в том числе новые
  selectedOrganizations: [],
  showGroups: false, // «Дополнительно»: группы терминалов (по умолчанию все — выбирать обычно не нужно)
  groups: null, // группы терминалов выбранных организаций — загружаются при раскрытии
  allGroups: true,
  selectedGroups: [],
};

// Смена организаций — группы терминалов снова «все», список групп загрузится заново
const withOrganizations = (prev, changes) => ({
  ...prev,
  ...changes,
  showGroups: false,
  groups: null,
  allGroups: true,
  selectedGroups: [],
});

function CreateConnectionDialog({ open, token, servers, onClose, onCreated }) {
  const desktop = useDesktopLayout();
  const [form, setForm] = useState(emptyCreate);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(emptyCreate);
      setStatus(null);
    }
  }, [open]);

  const freeServers = servers.filter((s) => !s.connected);
  const organizationIds = form.allOrganizations ? null : form.selectedOrganizations;

  const run = async (action) => {
    setBusy(true);
    setStatus(null);
    try {
      await action();
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const handleCheck = () =>
    run(async () => {
      const { organizations } = await checkIikoCloudKey(token, form.apiKey);
      setForm((prev) => withOrganizations(prev, { organizations, allOrganizations: true, selectedOrganizations: [] }));
    });

  const handleShowGroups = () => {
    if (form.showGroups) {
      setForm((prev) => ({ ...prev, showGroups: false }));
      return;
    }
    if (!form.allOrganizations && form.selectedOrganizations.length === 0) {
      setStatus({ type: 'error', text: 'Сначала выберите организации.' });
      return;
    }
    setForm((prev) => ({ ...prev, showGroups: true }));
    if (form.groups === null) {
      run(async () => {
        const groups = await fetchIikoCloudTerminalGroupsByKey(token, form.apiKey, organizationIds);
        setForm((prev) => ({ ...prev, groups }));
      });
    }
  };

  const handleCreate = () => {
    if (!form.allOrganizations && form.selectedOrganizations.length === 0) {
      setStatus({ type: 'error', text: 'Выберите хотя бы одну организацию или «Все организации».' });
      return;
    }
    if (!form.allGroups && form.selectedGroups.length === 0) {
      setStatus({ type: 'error', text: 'Выберите хотя бы одну группу терминалов или «Все группы».' });
      return;
    }
    run(async () => {
      await createIikoCloudConnection(token, {
        server_key: form.serverKey,
        api_key: form.apiKey,
        organization_ids: organizationIds,
        terminal_group_ids: form.allGroups ? null : form.selectedGroups,
      });
      onCreated();
    });
  };

  const close = () => !busy && onClose();
  const canCheck = form.serverKey && form.apiKey.trim() && !busy;
  const canSave = canCheck && form.organizations !== null && form.organizations.length > 0;
  // Серверов у админа десятки: на десктопе диалог шире и список в две колонки
  const wide = desktop && freeServers.length > 6;

  return (
    <ResponsiveDialog open={open} onClose={close} labelledBy="iikocloud-create-title" maxWidth={wide ? 760 : undefined}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DialogHeader id="iikocloud-create-title" title="Подключить iikoCloud" onClose={close} disabled={busy} />

        <Box>
          <SectionTitle sx={{ mt: 0 }}>Сервер iiko</SectionTitle>
          {freeServers.length === 0 ? (
            <Typography sx={{ fontSize: '14px', color: MUTED_TEXT_COLOR }}>Все серверы пользователей уже подключены.</Typography>
          ) : (
            <ChoiceList
              label="Сервер iiko"
              value={form.serverKey}
              disabled={busy}
              columns={wide ? 2 : 1}
              onChange={(serverKey) => setForm((prev) => ({ ...prev, serverKey }))}
              options={freeServers.map((s) => ({
                value: s.server_key,
                label: s.name,
                hint: `${s.server_key} · пользователей: ${s.users}`,
              }))}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <PasswordField
            id="iikocloud-api-key"
            label="Ключ API iikoCloud"
            hint="Отдельный ключ, выпущенный только для приложения"
            value={form.apiKey}
            onChange={(e) => {
              const apiKey = e.target.value;
              setForm((prev) => withOrganizations(prev, { apiKey, organizations: null }));
            }}
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
          />
          {form.organizations === null && (
            <Button type="button" onClick={handleCheck} disabled={!canCheck} sx={{ ...secondaryButtonSx, flex: '0 0 auto', alignSelf: 'flex-start', backgroundColor: 'rgba(255,255,255,0.06)' }}>
              <BusyLabel busy={busy}>Проверить ключ</BusyLabel>
            </Button>
          )}
        </Box>

        {form.organizations !== null && (
          <Box>
            <SectionTitle sx={{ mt: 0 }}>Организации</SectionTitle>
            <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR, mb: 0.5 }}>
              У сети на одном сервере каждая организация — ресторан.
            </Typography>
            <OrganizationsToggles
              organizations={form.organizations}
              allOrganizations={form.allOrganizations}
              selected={form.selectedOrganizations}
              disabled={busy}
              onAllChange={(allOrganizations) => setForm((prev) => withOrganizations(prev, { allOrganizations }))}
              onToggle={(id) =>
                setForm((prev) => withOrganizations(prev, { selectedOrganizations: toggleIn(prev.selectedOrganizations, id) }))
              }
            />
          </Box>
        )}

        {form.organizations !== null && form.organizations.length > 0 && (
          <GroupsAdvanced
            open={form.showGroups}
            onToggle={handleShowGroups}
            disabled={busy}
            summary={form.allGroups ? 'Все группы — обычно менять не нужно' : `Выбрано: ${form.selectedGroups.length}`}
          >
            {form.groups !== null ? (
              <GroupsToggles
                groups={form.groups}
                organizations={form.organizations}
                allGroups={form.allGroups}
                selected={form.selectedGroups}
                disabled={busy}
                onAllChange={(allGroups) => setForm((prev) => ({ ...prev, allGroups }))}
                onToggle={(id) => setForm((prev) => ({ ...prev, selectedGroups: toggleIn(prev.selectedGroups, id) }))}
              />
            ) : (
              busy && <Loading />
            )}
          </GroupsAdvanced>
        )}

        <StatusText status={status} />

        <Footer>
          <Button type="button" onClick={close} disabled={busy} sx={secondaryButtonSx}>
            Отмена
          </Button>
          <Button type="button" onClick={handleCreate} disabled={!canSave} sx={primaryButtonSx}>
            <BusyLabel busy={busy && canSave}>Подключить</BusyLabel>
          </Button>
        </Footer>
      </Box>
    </ResponsiveDialog>
  );
}

// «Дополнительно: группы терминалов» — свёрнуто: по умолчанию все группы, выбирать обычно не нужно
function GroupsAdvanced({ open, onToggle, disabled, summary, children }) {
  return (
    <Box>
      <ButtonBase onClick={onToggle} disabled={disabled} aria-expanded={open} sx={ACCORDION_BUTTON_SX}>
        <Box component="span" sx={{ minWidth: 0 }}>
          <Typography component="span" sx={{ display: 'block', fontSize: '15px', fontWeight: 600 }}>
            Дополнительно: группы терминалов
          </Typography>
          <Typography component="span" sx={{ display: 'block', fontSize: '13px', color: MUTED_TEXT_COLOR }}>
            {summary}
          </Typography>
        </Box>
        <KeyboardArrowDownIcon
          aria-hidden="true"
          sx={{ flexShrink: 0, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </ButtonBase>
      {open && children}
    </Box>
  );
}

// --- окно подключения: сведения и действия ---

function DetailRow({ label, value, first, color }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        py: 1,
        borderTop: first ? 'none' : `1px solid ${DIVIDER_COLOR}`,
      }}
    >
      <Box component="dt" sx={{ fontSize: '14px', color: MUTED_TEXT_COLOR, flexShrink: 0 }}>
        {label}
      </Box>
      <Box component="dd" sx={{ m: 0, fontSize: '14px', color: color || '#f5f5f5', textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </Box>
    </Box>
  );
}

function ConnectionDialog({ connection, token, onClose, onChanged, onDeleted }) {
  const [view, setView] = useState('main'); // main | key | groups | places | delete
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const open = Boolean(connection);

  useEffect(() => {
    if (open) {
      setView('main');
      setStatus(null);
    }
  }, [open, connection?.id]);

  if (!connection) return <ResponsiveDialog open={false} onClose={onClose} />;

  const run = async (action, successText) => {
    setBusy(true);
    setStatus(null);
    try {
      await action();
      if (successText) setStatus({ type: 'success', text: successText });
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const back = (nextStatus = null) => {
    setView('main');
    setStatus(nextStatus);
  };
  const close = () => !busy && onClose();
  const c = connection;
  const st = connectionStatus(c);
  // «Войти заново»: ключ не принят, пауза после ошибок или свежая ошибка — новый токен и снятие паузы
  const relogin = c.key_invalid || Object.keys(c.pauses?.backoff || {}).length > 0 || hasFreshError(c);

  return (
    <ResponsiveDialog open={open} onClose={close} labelledBy="iikocloud-connection-title">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DialogHeader
          id="iikocloud-connection-title"
          title={
            {
              main: c.server_name,
              key: 'Заменить ключ',
              organizations: 'Организации',
              groups: 'Группы терминалов',
              places: 'Места приготовления',
              groupMap: 'Группы ресторана',
              restaurantMap: 'Рестораны',
              delete: 'Удалить подключение',
            }[view]
          }
          subtitle={view === 'main' ? c.server_key : c.server_name}
          onClose={close}
          disabled={busy}
        />

        {view === 'main' && (
          <>
            <Box component="dl" sx={{ m: 0 }}>
              <DetailRow first label="Состояние" value={st.label} color={st.color} />
              <DetailRow label="Организации" value={organizationsSummary(c.organizations)} />
              <DetailRow label="Группы терминалов" value={groupsSummary(c.terminal_group_ids)} />
              <DetailRow label="Ключ" value={`••••${c.api_key_last4}`} />
              <DetailRow label="Последний успех" value={formatTime(c.last_ok_at)} />
              <DetailRow label="Последний лимит (429)" value={formatTime(c.last_rate_limited_at)} />
              {hasFreshError(c) && <DetailRow label={`Ошибка ${formatTime(c.last_error_at)}`} value={c.last_error} color={ERROR_COLOR} />}
              {c.nomenclature_issue && (
                <DetailRow
                  label={`Названия блюд ${formatTime(c.nomenclature_issue.at)}`}
                  value={nomenclatureIssueText(c.nomenclature_issue)}
                  color={WARNING_COLOR}
                />
              )}
            </Box>

            <ToggleRow
              id="iikocloud-enabled"
              first
              label="Подключение включено"
              hint="Выключенное не используется в отчётах, но его можно настраивать"
              checked={Boolean(c.enabled)}
              disabled={busy}
              onChange={(enabled) => run(async () => onChanged(await updateIikoCloudConnection(token, c.id, { enabled })))}
            />

            <Box>
              {relogin && (
                <ActionRow
                  first
                  icon={VerifiedIcon}
                  label={c.key_invalid ? 'Проверить снова' : 'Войти заново'}
                  hint={
                    c.key_invalid
                      ? 'iikoCloud не принял ключ — проверить тот же ключ ещё раз'
                      : 'Снять паузу после ошибок iikoCloud и получить новый токен'
                  }
                  disabled={busy}
                  onClick={() =>
                    run(
                      async () => onChanged(await recheckIikoCloudConnection(token, c.id)),
                      c.key_invalid ? 'Ключ принят iikoCloud.' : 'Пауза снята, ключ принят iikoCloud.'
                    )
                  }
                />
              )}
              <ActionRow
                first={!relogin}
                icon={StoreMallDirectoryIcon}
                label="Организации"
                hint="У сети на одном сервере каждая организация — ресторан"
                disabled={busy || c.key_invalid}
                onClick={() => setView('organizations')}
              />
              <ActionRow icon={ListAltIcon} label="Группы терминалов" disabled={busy || c.key_invalid} onClick={() => setView('groups')} />
              <ActionRow
                icon={SoupKitchenIcon}
                label="Места приготовления"
                hint="Что считается кухней в «Загрузке кухни»"
                disabled={busy || c.key_invalid}
                onClick={() => setView('places')}
              />
              {/* Сеть (несколько организаций или «все»): какой ресторан iiko — какая организация iikoCloud */}
              {(c.organizations == null || c.organizations.length > 1) && (
                <ActionRow
                  icon={StoreMallDirectoryIcon}
                  label="Рестораны"
                  hint="Какому ресторану iiko соответствует организация — для «Кухни» сети"
                  disabled={busy || c.key_invalid}
                  onClick={() => setView('restaurantMap')}
                />
              )}
              {/* Только когда есть что сопоставлять: у ресторана больше одной группы iiko (или отчёта ещё не было) */}
              {c.group_map_needed !== false && (
                <ActionRow
                  icon={StorefrontIcon}
                  label="Группы ресторана"
                  hint="Какой группе iiko соответствует группа терминалов — для открытых заказов"
                  disabled={busy || c.key_invalid}
                  onClick={() => setView('groupMap')}
                />
              )}
              <ActionRow
                icon={SyncIcon}
                label="Обновить справочники"
                disabled={busy || c.key_invalid}
                onClick={() => run(() => refreshIikoCloudDictionaries(token, c.id), 'Справочники обновлены из iikoCloud.')}
              />
              <ActionRow icon={KeyIcon} label="Заменить ключ" disabled={busy} onClick={() => setView('key')} />
              <ActionRow icon={DeleteOutlineIcon} label="Удалить подключение" danger disabled={busy} onClick={() => setView('delete')} />
            </Box>

            {busy && <Loading />}
            <StatusText status={status} />
          </>
        )}

        {view === 'key' && <ReplaceKeyView connection={c} token={token} onCancel={() => back()} onDone={(updated, warning) => {
          onChanged(updated);
          back(warning ? { type: 'warning', text: warning } : { type: 'success', text: 'Ключ заменён и проверен.' });
        }} />}

        {view === 'organizations' && <OrganizationsView connection={c} token={token} onCancel={() => back()} onDone={(updated) => {
          onChanged(updated);
          back({ type: 'success', text: 'Организации сохранены.' });
        }} />}

        {view === 'groups' && <GroupsView connection={c} token={token} onCancel={() => back()} onDone={(updated) => {
          onChanged(updated);
          back({ type: 'success', text: 'Группы терминалов сохранены.' });
        }} />}

        {view === 'places' && <PlacesView connection={c} token={token} onCancel={() => back()} onDone={() => back({ type: 'success', text: 'Места кухни сохранены.' })} />}

        {view === 'restaurantMap' && <RestaurantMapView connection={c} token={token} onCancel={() => back()} onDone={() => back({ type: 'success', text: 'Рестораны сохранены.' })} />}

        {view === 'groupMap' && <GroupMapView connection={c} token={token} onCancel={() => back()} onDone={() => back({ type: 'success', text: 'Группы ресторана сохранены.' })} />}

        {view === 'delete' && (
          <>
            <Typography role="alert" sx={{ fontSize: '15px', color: '#e4e4e7' }}>
              {`Удалить подключение iikoCloud для «${c.server_name}»? Отчёты iikoCloud по этому серверу станут недоступны.`}
            </Typography>
            <StatusText status={status} />
            <Footer>
              <Button type="button" onClick={() => back()} disabled={busy} sx={secondaryButtonSx}>
                Отмена
              </Button>
              <Button
                type="button"
                disabled={busy}
                sx={dangerButtonSx}
                onClick={() => run(async () => {
                  await deleteIikoCloudConnection(token, c.id);
                  onDeleted();
                })}
              >
                <BusyLabel busy={busy}>Удалить</BusyLabel>
              </Button>
            </Footer>
          </>
        )}
      </Box>
    </ResponsiveDialog>
  );
}

function ReplaceKeyView({ connection, token, onCancel, onDone }) {
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const result = await replaceIikoCloudKey(token, connection.id, apiKey);
      onDone(result.connection, result.warning);
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>
        Сейчас ключ ••••{connection.api_key_last4}. Новый ключ будет проверен в iikoCloud; подключение и настройки сохранятся.
      </Typography>
      <PasswordField
        id="iikocloud-new-key"
        label="Новый ключ API iikoCloud"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        disabled={busy}
        autoFocus
      />
      <StatusText status={status} />
      <Footer>
        <Button type="button" onClick={onCancel} disabled={busy} sx={secondaryButtonSx}>
          Отмена
        </Button>
        <Button type="submit" disabled={busy || !apiKey.trim()} sx={primaryButtonSx}>
          <BusyLabel busy={busy}>Проверить и заменить</BusyLabel>
        </Button>
      </Footer>
    </Box>
  );
}

// Организации привязки: «Все» (и новые) или выбранные. Смена набора — группы терминалов снова «все»
function OrganizationsView({ connection, token, onCancel, onDone }) {
  const [allOrganizations, setAllOrganizations] = useState(connection.organizations == null);
  const [selected, setSelected] = useState((connection.organizations || []).map((o) => o.id));
  const [busy, setBusy] = useState(false);
  // Загрузка из iikoCloud; при ошибке — таймер паузы и «Запросить» вместо «Сохранить»
  const { data: organizations, status, setStatus, retryAt, reload } = useCloudLoad(
    () => refreshIikoCloudDictionaries(token, connection.id).then((data) => data.organizations || []),
    [token, connection.id]
  );

  const handleSave = async () => {
    if (!allOrganizations && selected.length === 0) {
      setStatus({ type: 'error', text: 'Выберите хотя бы одну организацию или «Все организации».' });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      onDone(await updateIikoCloudConnection(token, connection.id, { organization_ids: allOrganizations ? null : selected }));
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
      setBusy(false);
    }
  };

  return (
    <>
      {connection.terminal_group_ids != null && (
        <Typography sx={{ fontSize: '14px', color: MUTED_TEXT_COLOR }}>
          При смене организаций группы терминалов снова станут «все».
        </Typography>
      )}
      {organizations === null && !status ? (
        <Loading />
      ) : organizations !== null ? (
        <OrganizationsToggles
          organizations={organizations}
          allOrganizations={allOrganizations}
          selected={selected}
          disabled={busy}
          onAllChange={setAllOrganizations}
          onToggle={(id) => setSelected((prev) => toggleIn(prev, id))}
        />
      ) : null}
      <StatusText status={status} />
      <Footer>
        <Button type="button" onClick={onCancel} disabled={busy} sx={secondaryButtonSx}>
          Отмена
        </Button>
        {organizations === null && status ? (
          <RetryButton retryAt={retryAt} onRetry={reload} />
        ) : (
          <Button type="button" onClick={handleSave} disabled={busy || organizations === null} sx={primaryButtonSx}>
            <BusyLabel busy={busy}>Сохранить</BusyLabel>
          </Button>
        )}
      </Footer>
    </>
  );
}

function GroupsView({ connection, token, onCancel, onDone }) {
  const [allGroups, setAllGroups] = useState(connection.terminal_group_ids == null);
  const [selected, setSelected] = useState(connection.terminal_group_ids || []);
  const [busy, setBusy] = useState(false);
  // Загрузка из iikoCloud; при ошибке — таймер паузы и «Запросить» вместо «Сохранить»
  const { data: groups, status, setStatus, retryAt, reload } = useCloudLoad(
    () => fetchIikoCloudTerminalGroups(token, connection.id),
    [token, connection.id]
  );

  const handleSave = async () => {
    if (!allGroups && selected.length === 0) {
      setStatus({ type: 'error', text: 'Выберите хотя бы одну группу терминалов или «Все группы».' });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      onDone(await updateIikoCloudConnection(token, connection.id, { terminal_group_ids: allGroups ? null : selected }));
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
      setBusy(false);
    }
  };

  return (
    <>
      <Typography sx={{ fontSize: '14px', color: MUTED_TEXT_COLOR }}>{organizationsSummary(connection.organizations)}</Typography>
      {groups === null && !status ? (
        <Loading />
      ) : groups !== null ? (
        <GroupsToggles
          groups={groups}
          organizations={connection.organizations}
          allGroups={allGroups}
          selected={selected}
          disabled={busy}
          onAllChange={setAllGroups}
          onToggle={(id) => setSelected((prev) => toggleIn(prev, id))}
        />
      ) : null}
      <StatusText status={status} />
      <Footer>
        <Button type="button" onClick={onCancel} disabled={busy} sx={secondaryButtonSx}>
          Отмена
        </Button>
        {groups === null && status ? (
          <RetryButton retryAt={retryAt} onRetry={reload} />
        ) : (
          <Button type="button" onClick={handleSave} disabled={busy || groups === null} sx={primaryButtonSx}>
            <BusyLabel busy={busy}>Сохранить</BusyLabel>
          </Button>
        )}
      </Footer>
    </>
  );
}

function placeHint(place) {
  if (place.fixed) return 'всегда кухня';
  const parts = [];
  if (place.is_new) parts.push('новое');
  if (place.items_30d != null) parts.push(`${Math.round(place.items_30d)} поз. за 30 дней`);
  return parts.join(' · ') || undefined;
}

function PlacesView({ connection, token, onCancel, onDone }) {
  const [busy, setBusy] = useState(false);
  // Загрузка из iikoCloud; при ошибке — таймер паузы и «Запросить» вместо «Сохранить» · { saved, places }
  const { data: state, setData: setState, status, setStatus, retryAt, reload } = useCloudLoad(
    () => fetchIikoCloudPlaces(token, connection.id),
    [token, connection.id]
  );

  const toggle = (name) =>
    setState((prev) => ({ ...prev, places: prev.places.map((p) => (p.name === name ? { ...p, kitchen: !p.kitchen } : p)) }));

  const handleSave = async () => {
    setBusy(true);
    setStatus(null);
    try {
      await saveIikoCloudPlaces(
        token,
        connection.id,
        state.places.filter((p) => p.kitchen).map((p) => p.name),
        state.places.map((p) => p.name)
      );
      onDone();
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
      setBusy(false);
    }
  };

  return (
    <>
      <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>
        Места за последние 30 дней. Включённые считаются кухней в «Загрузке кухни», остальные (бар) не учитываются.
      </Typography>
      {state?.stale && (
        <StatusText
          status={{
            type: 'warning',
            text: 'iikoCloud сейчас не строит отчёты — показаны известные места без числа позиций. Настройку можно сохранить.',
          }}
        />
      )}
      {state && !state.saved && (
        <StatusText
          status={{
            type: 'warning',
            text: 'Настройка ещё не сохранена: сейчас места с «бар» или «bar» в названии не считаются кухней. Проверьте и сохраните.',
          }}
        />
      )}
      {state === null && !status ? (
        <Loading />
      ) : state && state.places.length === 0 ? (
        <Typography sx={{ fontSize: '14px', color: MUTED_TEXT_COLOR }}>За 30 дней продаж с местами приготовления не было.</Typography>
      ) : state ? (
        <Box>
          {state.places.map((p, index) => (
            <ToggleRow
              key={p.name}
              id={`iikocloud-place-${index}`}
              first={index === 0}
              label={p.name}
              hint={placeHint(p)}
              checked={p.kitchen}
              disabled={busy || p.fixed}
              onChange={() => toggle(p.name)}
            />
          ))}
        </Box>
      ) : null}
      <StatusText status={status} />
      <Footer>
        <Button type="button" onClick={onCancel} disabled={busy} sx={secondaryButtonSx}>
          Отмена
        </Button>
        {state === null && status ? (
          <RetryButton retryAt={retryAt} onRetry={reload} />
        ) : (
          <Button type="button" onClick={handleSave} disabled={busy || !state || state.places.length === 0} sx={primaryButtonSx}>
            <BusyLabel busy={busy}>Сохранить</BusyLabel>
          </Button>
        )}
      </Footer>
    </>
  );
}

// Группа терминалов iikoCloud → группа iiko из выбора ресторана: по ней фильтруются открытые заказы
function GroupMapView({ connection, token, onCancel, onDone }) {
  const [busy, setBusy] = useState(false);
  // Загрузка из iikoCloud; при ошибке — таймер паузы и «Запросить» вместо «Сохранить» · { saved, needed, terminal_groups, map }
  const { data: state, setData: setState, status, setStatus, retryAt, reload } = useCloudLoad(
    () => fetchIikoCloudGroupMap(token, connection.id),
    [token, connection.id]
  );

  // У сети одинаковые названия групп терминалов в разных ресторанах — в заголовке ещё и организация
  const severalOrganizations = new Set((state?.terminal_groups || []).map((tg) => tg.organization)).size > 1;

  const choose = (terminalGroupId, value) =>
    setState((prev) => ({ ...prev, map: { ...prev.map, [terminalGroupId]: value || null } }));

  const handleSave = async () => {
    setBusy(true);
    setStatus(null);
    try {
      await saveIikoCloudGroupMap(token, connection.id, state.map);
      onDone();
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
      setBusy(false);
    }
  };

  return (
    <>
      <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>
        Открытые заказы iikoCloud знают только группу терминалов. Выберите, какой группе iiko (как в выборе ресторана на
        экранах) она соответствует.
      </Typography>
      {state?.stale && (
        <StatusText status={{ type: 'warning', text: 'iikoCloud сейчас не строит отчёты — группы iiko из последнего удачного отчёта.' }} />
      )}
      {state && !state.needed && (
        <StatusText status={{ type: 'info', text: 'У каждого ресторана одна группа iiko — сопоставление однозначно, менять его не нужно.' }} />
      )}
      {state && state.needed && !state.saved && (
        <StatusText status={{ type: 'warning', text: 'Ещё не сохранено: группы сопоставлены по совпадению названий. Проверьте и сохраните.' }} />
      )}
      {state === null && !status ? (
        <Loading />
      ) : state ? (
        state.terminal_groups.map((tg) => (
          <Box key={tg.id}>
            <SectionTitle sx={{ mt: 0 }}>
              {[tg.name, severalOrganizations ? tg.organization : null, tg.in_sleep ? 'спящий режим' : null]
                .filter(Boolean)
                .join(' · ')}
            </SectionTitle>
            <ChoiceList
              label={`Группа iiko для «${tg.name}»`}
              value={state.map[tg.id] || ''}
              disabled={busy}
              onChange={(value) => choose(tg.id, value)}
              options={[
                // У сети — только группы своего ресторана
                ...tg.candidates.map((name) => ({ value: name, label: name })),
                { value: '', label: 'Без группы', hint: 'Заказы этой группы терминалов видны только при «Все»' },
              ]}
            />
          </Box>
        ))
      ) : null}
      <StatusText status={status} />
      <Footer>
        <Button type="button" onClick={onCancel} disabled={busy} sx={secondaryButtonSx}>
          Отмена
        </Button>
        {state === null && status ? (
          <RetryButton retryAt={retryAt} onRetry={reload} />
        ) : (
          <Button type="button" onClick={handleSave} disabled={busy || !state} sx={primaryButtonSx}>
            <BusyLabel busy={busy}>Сохранить</BusyLabel>
          </Button>
        )}
      </Footer>
    </>
  );
}

// Организация iikoCloud → ресторан iiko RMS (как в выборе ресторана): по нему «Кухня» сети знает, чьи это
// открытые заказы и стоп-лист. Рестораны — из отчётов «Выручки» этого сервера
function RestaurantMapView({ connection, token, onCancel, onDone }) {
  const [busy, setBusy] = useState(false);
  // Загрузка; при ошибке — таймер паузы и «Запросить» вместо «Сохранить» · { saved, organizations, restaurants, map }
  const { data: state, setData: setState, status, setStatus, retryAt, reload } = useCloudLoad(
    () => fetchIikoCloudRestaurantMap(token, connection.id),
    [token, connection.id]
  );

  const choose = (organizationId, value) =>
    setState((prev) => ({ ...prev, map: { ...prev.map, [organizationId]: value || null } }));

  const handleSave = async () => {
    setBusy(true);
    setStatus(null);
    try {
      await saveIikoCloudRestaurantMap(token, connection.id, state.map);
      onDone();
    } catch (err) {
      setStatus({ type: 'error', text: extractErrorMessage(err) });
      setBusy(false);
    }
  };

  return (
    <>
      <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>
        Выберите, какому ресторану iiko (как в выборе ресторана на экранах) соответствует каждая организация
        iikoCloud. Рестораны без организации на «Кухне» недоступны.
      </Typography>
      {state && state.restaurants.length === 0 && (
        <StatusText
          status={{ type: 'info', text: 'Список ресторанов появится, когда кто-нибудь откроет «Выручку» этого сервера. Пока предложены названия организаций.' }}
        />
      )}
      {state && !state.saved && (
        <StatusText status={{ type: 'warning', text: 'Ещё не сохранено: рестораны сопоставлены по совпадению названий. Проверьте и сохраните.' }} />
      )}
      {state === null && !status ? (
        <Loading />
      ) : state ? (
        state.organizations.map((organization) => {
          const current = state.map[organization.id] || '';
          // Текущее значение — в списке, даже если ресторана нет среди известных (автоподстановка или сохранённое)
          const names = [...new Set([...state.restaurants, ...(current ? [current] : [])])];
          return (
            <Box key={organization.id}>
              <SectionTitle sx={{ mt: 0 }}>{organization.name || organization.id}</SectionTitle>
              <ChoiceList
                label={`Ресторан для «${organization.name || organization.id}»`}
                value={current}
                disabled={busy}
                onChange={(value) => choose(organization.id, value)}
                options={[
                  ...names.map((name) => ({ value: name, label: name })),
                  { value: '', label: 'Не сопоставлена', hint: 'Её открытые заказы и стоп-лист на «Кухне» не показываются' },
                ]}
              />
            </Box>
          );
        })
      ) : null}
      <StatusText status={status} />
      <Footer>
        <Button type="button" onClick={onCancel} disabled={busy} sx={secondaryButtonSx}>
          Отмена
        </Button>
        {state === null && status ? (
          <RetryButton retryAt={retryAt} onRetry={reload} />
        ) : (
          <Button type="button" onClick={handleSave} disabled={busy || !state} sx={primaryButtonSx}>
            <BusyLabel busy={busy}>Сохранить</BusyLabel>
          </Button>
        )}
      </Footer>
    </>
  );
}
